import { Input } from "antd";
import type { InputRef } from "antd";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "../app/styles";
import { resolveScanInputMode } from "../common/utils/scanInputMode";

export type ScanValidateState = "idle" | "ok" | "error";

interface ScanFieldProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  /** Fired when the wedge Enter suffix (or manual Enter) commits the field. */
  onScan?: (value: string) => void;
  validateState?: ScanValidateState;
  /** Allow tap-to-type (reveals the soft keyboard). Default true. */
  allowManual?: boolean;
  /** Auto-focus on mount. Default true. */
  autoFocus?: boolean;
  /**
   * Change this value to force a re-focus + select-all (e.g. when a multi-field
   * screen advances the active field without remounting).
   */
  focusSignal?: unknown;
  disabled?: boolean;
  /** Overrides the default "AWAITING SCAN…" hint. */
  hint?: string;
}

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 700;
  font-size: 14px;
  color: ${colors.primaryDark};
`;

const Hint = styled.span<{ $tone: ScanValidateState }>`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${(p) =>
    p.$tone === "error"
      ? colors.danger
      : p.$tone === "ok"
      ? colors.primary
      : colors.textMuted};
`;

const borderFor = (state: ScanValidateState) =>
  state === "error"
    ? colors.danger
    : state === "ok"
    ? colors.primary
    : "#cfd8d4";

const StyledInput = styled(Input)<{ $state: ScanValidateState }>`
  height: 52px;
  border-radius: 10px;
  border: 2px solid ${(p) => borderFor(p.$state)};
  font-size: 18px !important;
  font-weight: 600;

  input {
    font-size: 18px !important;
    font-weight: 600;
  }
`;

export default function ScanField({
  label,
  placeholder,
  value,
  onChange,
  onScan,
  validateState = "idle",
  allowManual = true,
  autoFocus = true,
  focusSignal,
  disabled,
  hint,
}: ScanFieldProps) {
  const inputRef = useRef<InputRef>(null);
  const [tapped, setTapped] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    // `focusSignal` is a dependency so the field re-focuses when a multi-field
    // step advances the active field without remounting.
    void focusSignal;
    if (autoFocus && !disabled) {
      // `cursor: "all"` selects existing text so a re-scan overwrites it.
      inputRef.current?.focus({ cursor: "all" });
    }
  }, [autoFocus, disabled, focusSignal]);

  const handleTap = () => {
    if (!allowManual) {
      return;
    }
    // Flip inputMode synchronously inside the tap gesture so the soft keyboard
    // opens on THIS tap (a React re-render would be one gesture too late).
    const el = inputRef.current?.input;
    if (el) {
      el.inputMode = "text";
    }
    setTapped(true);
  };

  const showAwaiting = focused && value.trim() === "" && !disabled;
  const hintText = hint ?? (showAwaiting ? "AWAITING SCAN…" : "");
  const hintTone: ScanValidateState =
    validateState !== "idle" ? validateState : showAwaiting ? "idle" : "idle";

  return (
    <Field>
      {(label || hintText) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          {label ? <Label>{label}</Label> : <span />}
          {hintText && <Hint $tone={hintTone}>{hintText}</Hint>}
        </div>
      )}
      <StyledInput
        ref={inputRef}
        $state={validateState}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        inputMode={allowManual ? resolveScanInputMode(tapped) : "none"}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={handleTap}
        onChange={(e) => onChange(e.target.value)}
        onPressEnter={() => onScan?.(value)}
      />
    </Field>
  );
}
